import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageCircle, X, Trash2, UserPlus } from 'lucide-react';
import type { LeadRow, LeadStage, LeadSource } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Modal, Input, Select, Textarea, Skeleton, EmptyState, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { getLeads, createLead, updateLead, deleteLead } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { waDigits } from '../lib/format';

const STAGES: { key: LeadStage; label: string; tone: string }[] = [
  { key: 'NEW', label: 'New', tone: 'bg-surface text-ink-soft' },
  { key: 'CONTACTED', label: 'Contacted', tone: 'bg-surface text-ink-soft' },
  { key: 'TRIAL', label: 'Trial', tone: 'bg-accent-soft text-ink' },
  { key: 'JOINED', label: 'Joined', tone: 'bg-ok-soft text-ok-ink' },
  { key: 'LOST', label: 'Lost', tone: 'bg-bad-soft text-bad-ink' },
];

const SOURCES: { key: LeadSource; label: string }[] = [
  { key: 'WALK_IN', label: 'Walk-in' },
  { key: 'PHONE', label: 'Phone' },
  { key: 'WHATSAPP', label: 'WhatsApp' },
  { key: 'WEB', label: 'Web' },
  { key: 'REFERRAL', label: 'Referral' },
  { key: 'GUEST_PASS', label: 'Guest pass' },
  { key: 'OTHER', label: 'Other' },
];
const sourceLabel = (s: LeadSource) => SOURCES.find((x) => x.key === s)?.label ?? s;

/** The sales pipeline. Prospects (walk-ins, calls, and every invited guest) move through stages
 *  until they join or are lost, so no lead falls through the cracks. */
export function LeadsPage() {
  const { data, loading, error, reload } = useAsync(getLeads);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<LeadRow | null>(null);

  const leads = useMemo(() => data ?? [], [data]);
  const byStage = useMemo(() => {
    const map: Record<LeadStage, LeadRow[]> = { NEW: [], CONTACTED: [], TRIAL: [], JOINED: [], LOST: [] };
    for (const l of leads) map[l.stage].push(l);
    return map;
  }, [leads]);

  const joined = byStage.JOINED.length;
  const rate = leads.length ? Math.round((joined / leads.length) * 100) : 0;

  async function move(lead: LeadRow, stage: LeadStage) {
    await updateLead(lead.id, { stage }).catch(() => undefined);
    reload();
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="Sales"
        title="Leads"
        description="Every prospect in one pipeline, including everyone invited on a guest pass. Move them along until they join."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Add lead</span>
          </Button>
        }
      />

      {loading ? (
        <Skeleton className="h-96" />
      ) : error ? (
        <EmptyState title="Couldn’t load leads" description={error} />
      ) : leads.length === 0 ? (
        <EmptyState
          title="No leads yet"
          description="Add a walk-in or phone enquiry, or invite a guest. Every guest pass shows up here automatically."
          className="bg-surface"
        />
      ) : (
        <>
          <p className="mb-3 text-sm text-muted">
            {leads.length} lead{leads.length === 1 ? '' : 's'} · {joined} joined ({rate}% conversion)
          </p>
          {/* Mobile: stages stack vertically (natural to scroll on a phone). Desktop: a scrolling
              kanban of columns. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:overflow-x-auto sm:pb-2">
            {STAGES.map((s) => (
              <div key={s.key} className="flex w-full shrink-0 flex-col sm:w-64">
                <div className="mb-2 flex items-center justify-between px-1">
                  <span className="text-sm font-semibold text-ink">{s.label}</span>
                  <span className={cn('rounded-pill px-2 py-0.5 text-xs font-medium', s.tone)}>{byStage[s.key].length}</span>
                </div>
                <div className="flex min-h-[3rem] flex-col gap-2 rounded-card bg-surface/60 p-2">
                  {byStage[s.key].map((lead) => (
                    <LeadCard key={lead.id} lead={lead} onOpen={() => setSelected(lead)} onMove={(stage) => move(lead, stage)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {addOpen && <AddLeadModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); reload(); }} />}
      {selected && (
        <LeadDetailModal
          lead={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); reload(); }}
        />
      )}
    </section>
  );
}

function LeadCard({ lead, onOpen, onMove }: { lead: LeadRow; onOpen: () => void; onMove: (s: LeadStage) => void }) {
  const wa = waDigits(lead.phone ?? '');
  function whatsapp(e: React.MouseEvent) {
    e.stopPropagation();
    if (wa) window.open(`https://wa.me/${wa}`, '_blank', 'noopener');
  }
  return (
    <div className="rounded-control bg-card p-3 shadow-card">
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <p className="truncate text-sm font-medium text-ink">{lead.name}</p>
        <p className="truncate text-xs text-muted tabular">{lead.phone ?? lead.email ?? 'No contact'}</p>
        <span className="mt-1.5 inline-block rounded-pill bg-surface px-2 py-0.5 text-[11px] text-ink-soft">
          {sourceLabel(lead.source)}
        </span>
      </button>
      <div className="mt-2 flex items-center gap-1.5">
        <select
          value={lead.stage}
          onChange={(e) => onMove(e.target.value as LeadStage)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Move ${lead.name} to another stage`}
          className="h-8 flex-1 rounded-control border border-line bg-elevated px-2 text-xs text-ink"
        >
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        {lead.phone && (
          <button
            type="button"
            onClick={whatsapp}
            aria-label={`WhatsApp ${lead.name}`}
            title={`WhatsApp ${lead.name}`}
            className="inline-flex size-8 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface hover:text-ink"
          >
            <MessageCircle className="size-4" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

function AddLeadModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState<LeadSource>('WALK_IN');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) {
      setError('A name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createLead({
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        source,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not add the lead. Please try again.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Add a lead</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Input label="Phone (optional)" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+263 77 000 0000" />
        <Input label="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Select label="Source" value={source} onChange={(e) => setSource(e.target.value as LeadSource)}>
          {SOURCES.filter((s) => s.key !== 'GUEST_PASS').map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </Select>
        <Textarea label="Notes (optional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} loading={saving}>Add lead</Button>
        </div>
      </div>
    </Modal>
  );
}

function LeadDetailModal({ lead, onClose, onChanged }: { lead: LeadRow; onClose: () => void; onChanged: () => void }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [name, setName] = useState(lead.name);
  const [phone, setPhone] = useState(lead.phone ?? '');
  const [email, setEmail] = useState(lead.email ?? '');
  const [stage, setStage] = useState<LeadStage>(lead.stage);
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [lostReason, setLostReason] = useState(lead.lostReason ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await updateLead(lead.id, {
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        stage,
        notes,
        lostReason: stage === 'LOST' ? lostReason.trim() || undefined : undefined,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save. Please try again.');
      setSaving(false);
    }
  }

  function convert() {
    const qs = new URLSearchParams();
    qs.set('name', name.trim());
    if (phone.trim()) qs.set('phone', phone.trim());
    if (email.trim()) qs.set('email', email.trim());
    qs.set('lead', lead.id);
    navigate(`/members/new?${qs.toString()}`);
  }

  async function remove() {
    const ok = await confirm({
      title: `Delete ${lead.name}?`,
      description: 'This removes the lead from the pipeline. It cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await deleteLead(lead.id).catch(() => undefined);
    onChanged();
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">Lead</h2>
            <p className="text-xs text-muted">From {sourceLabel(lead.source)}</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Select label="Stage" value={stage} onChange={(e) => setStage(e.target.value as LeadStage)}>
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </Select>
        {stage === 'LOST' && (
          <Input label="Reason lost (optional)" value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="e.g. too expensive" />
        )}
        <Textarea label="Notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes, what they want, follow-up dates…" />

        {error && <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">{error}</p>}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            {stage !== 'JOINED' && (
              <Button variant="outline" onClick={convert}>
                <UserPlus className="size-4" aria-hidden /> Convert to member
              </Button>
            )}
            <button type="button" onClick={remove} className="inline-flex items-center gap-1.5 rounded-control px-2.5 py-2 text-sm font-medium text-muted hover:bg-bad-soft hover:text-bad-ink">
              <Trash2 className="size-4" aria-hidden /> Delete
            </button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
